import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import RoomJoin from './components/RoomJoin';
import RoomCreation from './components/RoomCreation';
import ClipboardRoom from './components/ClipboardRoom';
import LegalDisclaimerModal from './components/LegalDisclaimerModal';
import CleanupMonitor from './components/CleanupMonitor';
import RoomCounter from './components/RoomCounter';

const {
  FiShare2, FiPlus, FiLogIn, FiZap, FiLock, FiSmartphone, FiActivity, FiCopy, 
  FiShield, FiCheckCircle, FiArrowRight, FiUsers, FiCode, FiUser, FiChevronDown, 
  FiChevronUp, FiClock, FiInfo, FiFileText, FiTrash2, FiRefreshCw
} = FiIcons;

function App() {
  const [currentView, setCurrentView] = useState('home'); // 'home', 'join', 'create', 'room'
  const [roomData, setRoomData] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showLegalDisclaimer, setShowLegalDisclaimer] = useState(false);
  const [showCleanupMonitor, setShowCleanupMonitor] = useState(process.env.NODE_ENV === 'development');

  const handleJoinRoom = (roomName, password) => {
    setRoomData({ roomName, password });
    setCurrentView('room');
  };

  const handleLeaveRoom = () => {
    setRoomData(null);
    setCurrentView('home');
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const renderHome = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="w-20 h-20 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiShare2} className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Share Clipboard Content{' '}
            <span className="text-primary-600">Across All Your Devices</span>{' '}
            Instantly
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Real-time clipboard synchronization with no apps to install. Secure, private, and works everywhere.
            <br />
            <span className="text-sm text-amber-600 font-medium">
              🔥 All rooms automatically delete after 1 hour for maximum privacy
            </span>
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <motion.button
              onClick={() => setCurrentView('create')}
              className="bg-primary-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-700 transition-all shadow-lg"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Create New Room
            </motion.button>
            <motion.button
              onClick={() => setCurrentView('join')}
              className="bg-white text-primary-600 border-2 border-primary-600 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-50 transition-all shadow-lg"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Join Existing Room
            </motion.button>
          </div>

          {/* Trust Signals */}
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <SafeIcon icon={FiCheckCircle} className="text-green-500" />
              <span>Works everywhere</span>
            </div>
            <div className="flex items-center gap-1">
              <SafeIcon icon={FiCheckCircle} className="text-green-500" />
              <span>No registration</span>
            </div>
            <div className="flex items-center gap-1">
              <SafeIcon icon={FiCheckCircle} className="text-green-500" />
              <span>Auto-delete after 1 hour</span>
            </div>
            <div className="flex items-center gap-1">
              <SafeIcon icon={FiCheckCircle} className="text-green-500" />
              <span>100% private</span>
            </div>
          </div>
        </motion.div>

        {/* Room Counter - NEW */}
        <RoomCounter />

        {/* Auto-Delete Feature Highlight */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-20 bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl p-8 border border-red-100"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiClock} className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              🔥 Maximum Privacy with Auto-Delete
            </h2>
            <p className="text-lg text-gray-700 mb-6 max-w-3xl mx-auto">
              Every room and all its content is <strong>permanently deleted after exactly 1 hour</strong>. 
              Room names, passwords, and all shared clipboard items are completely wiped from our servers.
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="bg-white rounded-lg p-4 border border-red-100">
                <SafeIcon icon={FiTrash2} className="w-6 h-6 text-red-500 mx-auto mb-2" />
                <div className="font-semibold text-gray-900">Complete Deletion</div>
                <div className="text-gray-600">Room names & passwords erased</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-red-100">
                <SafeIcon icon={FiRefreshCw} className="w-6 h-6 text-red-500 mx-auto mb-2" />
                <div className="font-semibold text-gray-900">Automatic Cleanup</div>
                <div className="text-gray-600">Runs every minute, no manual action</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-red-100">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-red-500 mx-auto mb-2" />
                <div className="font-semibold text-gray-900">Zero Traces</div>
                <div className="text-gray-600">No permanent records kept</div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Key Features */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-10 text-gray-900">
            <span className="text-primary-600">⭐</span> Key Features
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <SafeIcon icon={FiZap} className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">⚡ Instant Synchronization</h3>
              <p className="text-gray-600">Sub-100ms real-time sharing across all your connected devices.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <SafeIcon icon={FiLock} className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">🔒 Password-Protected Rooms</h3>
              <p className="text-gray-600">Secure access with automatically generated strong passwords.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <SafeIcon icon={FiClock} className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">⏰ Auto-Delete After 1 Hour</h3>
              <p className="text-gray-600">Complete data deletion ensures maximum privacy and security.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <SafeIcon icon={FiSmartphone} className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">📱 Cross-Platform Compatibility</h3>
              <p className="text-gray-600">Works seamlessly on all devices and modern browsers.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <SafeIcon icon={FiActivity} className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">📊 Real-Time Connection Status</h3>
              <p className="text-gray-600">Live user count and connection health monitoring.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <SafeIcon icon={FiCopy} className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">📋 One-Click Copy</h3>
              <p className="text-gray-600">Effortless clipboard operations with keyboard shortcuts.</p>
            </div>
          </div>
        </motion.section>

        {/* How It Works */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-20 bg-white rounded-2xl p-10 shadow-lg"
        >
          <h2 className="text-3xl font-bold text-center mb-10 text-gray-900">
            <span className="text-primary-600">🔄</span> How It Works
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                <SafeIcon icon={FiPlus} className="w-8 h-8 text-primary-600" />
                <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Create or Join a Room</h3>
              <p className="text-gray-600 text-sm">Auto-generated secure credentials for quick setup.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                <SafeIcon icon={FiSmartphone} className="w-8 h-8 text-primary-600" />
                <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect Your Devices</h3>
              <p className="text-gray-600 text-sm">Share credentials across all your devices securely.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                <SafeIcon icon={FiShare2} className="w-8 h-8 text-primary-600" />
                <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Sharing</h3>
              <p className="text-gray-600 text-sm">Real-time content synchronization between devices.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                <SafeIcon icon={FiClock} className="w-8 h-8 text-red-600" />
                <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center font-bold">4</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Auto-Delete</h3>
              <p className="text-gray-600 text-sm">Everything deleted automatically after 1 hour.</p>
            </div>
          </div>
          <div className="mt-10 flex justify-center">
            <motion.button
              onClick={() => setCurrentView('create')}
              className="flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-700 transition-all"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <span>Get Started Now</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.section>

        {/* Footer */}
        <footer className="text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} ClipShares - Secure Clipboard Sharing</p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiClock} className="w-4 h-4" />
              <span>All shared content automatically deleted after 1 hour</span>
            </div>
            <button
              onClick={() => setShowLegalDisclaimer(true)}
              className="flex items-center gap-1 text-primary-600 hover:text-primary-700 transition-colors"
            >
              <SafeIcon icon={FiFileText} className="w-4 h-4" />
              <span>Legal Disclaimer</span>
            </button>
            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={() => setShowCleanupMonitor(!showCleanupMonitor)}
                className="flex items-center gap-1 text-amber-600 hover:text-amber-700 transition-colors"
              >
                <SafeIcon icon={FiInfo} className="w-4 h-4" />
                <span>Cleanup Monitor</span>
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'join':
        return (
          <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
              <motion.button
                onClick={() => setCurrentView('home')}
                className="mb-6 text-primary-600 hover:text-primary-700 font-medium flex items-center gap-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                ← Back to Home
              </motion.button>
              <RoomJoin onJoinRoom={handleJoinRoom} />
            </div>
          </div>
        );
      case 'create':
        return (
          <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
              <motion.button
                onClick={() => setCurrentView('home')}
                className="mb-6 text-primary-600 hover:text-primary-700 font-medium flex items-center gap-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                ← Back to Home
              </motion.button>
              <RoomCreation onRoomCreated={handleJoinRoom} />
            </div>
          </div>
        );
      case 'room':
        return (
          <ClipboardRoom
            roomName={roomData.roomName}
            password={roomData.password}
            onLeaveRoom={handleLeaveRoom}
          />
        );
      default:
        return renderHome();
    }
  };

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          key={currentView}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderContent()}
        </motion.div>
      </AnimatePresence>

      {/* Cleanup Monitor (Development Only) */}
      {showCleanupMonitor && <CleanupMonitor />}

      {/* Legal Disclaimer Modal */}
      <LegalDisclaimerModal
        isOpen={showLegalDisclaimer}
        onClose={() => setShowLegalDisclaimer(false)}
      />

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 2000,
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </>
  );
}

export default App;